/**
 * Dropdown Positioning Utility
 * 
 * Calculates optimal position for dropdown menus to ensure they:
 * 1. Don't overflow horizontally (adjusts left position)
 * 2. Don't overflow vertically (limits max height)
 * 3. Stay within 100px of the bottom edge before scrolling
 * 
 * Also provides a global event system to close all dropdowns when:
 * - Another dropdown opens
 * - User zooms/pans the view
 */

const BOTTOM_MARGIN = 100; // Minimum distance from bottom of viewport
const HORIZONTAL_MARGIN = 8; // Minimum distance from right edge

// Global event name for closing all dropdowns
export const CLOSE_ALL_DROPDOWNS_EVENT = 'closeAllToolbarDropdowns';

// Event dispatched by flowchart when panning/zooming starts
export const FLOWCHART_PAN_START_EVENT = 'flowchartpanstart';

/**
 * Dispatch event to close all open dropdowns
 * Call this when opening a new dropdown or when zooming/panning
 * @param {Element} sourceElement - Optional element that triggered the close (for parent menus to check)
 */
export const closeAllDropdowns = (sourceElement = null) => {
  window.dispatchEvent(new CustomEvent(CLOSE_ALL_DROPDOWNS_EVENT, {
    detail: { sourceElement }
  }));
};

/**
 * Calculate dropdown position with edge detection
 * @param {DOMRect} buttonRect - The bounding rect of the trigger button
 * @param {number} dropdownWidth - Estimated or actual width of the dropdown (default 280)
 * @returns {Object} Position object with top, left, and maxHeight
 */
export const calculateDropdownPosition = (buttonRect, dropdownWidth = 280) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  // Calculate initial position (below the button)
  let top = buttonRect.bottom + 4;
  let left = buttonRect.left;
  
  // Check if dropdown would overflow on the right
  if (left + dropdownWidth > viewportWidth - HORIZONTAL_MARGIN) {
    // Align to right edge of button instead, or to viewport edge
    left = Math.max(HORIZONTAL_MARGIN, Math.min(
      buttonRect.right - dropdownWidth,
      viewportWidth - dropdownWidth - HORIZONTAL_MARGIN
    ));
  }
  
  // Calculate max height to stay within 100px of bottom
  const maxHeight = viewportHeight - top - BOTTOM_MARGIN;
  
  return {
    top,
    left,
    maxHeight: Math.max(maxHeight, 150), // Minimum height of 150px
  };
};

export default calculateDropdownPosition;
